import { EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Button, Input } from '@nextui-org/react';
import React from 'react';

import { ModernModal } from '@portal/components/common/ModernModal.ui';

interface UpdatePasswordModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  passwordData: {
    oldPassword: string;
    password: string;
    confirmPassword: string;
  };
  onPasswordDataChange: (data: { oldPassword: string; password: string; confirmPassword: string }) => void;
  showOldPassword: boolean;
  showNewPassword: boolean;
  showConfirmPassword: boolean;
  onShowOldPasswordChange: (show: boolean) => void;
  onShowNewPasswordChange: (show: boolean) => void;
  onShowConfirmPasswordChange: (show: boolean) => void;
  onSubmit: () => void;
  isLoading?: boolean;
}

export const UpdatePasswordModal: React.FC<UpdatePasswordModalProps> = ({
  isOpen,
  onOpenChange,
  passwordData,
  onPasswordDataChange,
  showOldPassword,
  showNewPassword,
  showConfirmPassword,
  onShowOldPasswordChange,
  onShowNewPasswordChange,
  onShowConfirmPasswordChange,
  onSubmit,
  isLoading,
}) => {
  const icon = <KeyIcon className='h-4 w-4' />;

  const footer = (
    <>
      <Button color='danger' variant='light' onPress={onOpenChange} disabled={isLoading}>
        Cancel
      </Button>
      <Button color='primary' onPress={onSubmit} isLoading={isLoading}>
        Update Password
      </Button>
    </>
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      onSubmit();
    } else if (e.key === 'Escape') {
      onOpenChange();
    }
  };

  return (
    <ModernModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title='Update Password'
      subtitle='Change your current password'
      icon={icon}
      size='md'
      footer={footer}
    >
      <div className='flex flex-col gap-4' onKeyDown={handleKeyDown}>
        <Input
          label='Current Password'
          placeholder='Enter current password'
          type={showOldPassword ? 'text' : 'password'}
          value={passwordData.oldPassword}
          onValueChange={value => onPasswordDataChange({ ...passwordData, oldPassword: value })}
          endContent={
            <button
              className='focus:outline-none'
              type='button'
              onClick={() => onShowOldPasswordChange(!showOldPassword)}
            >
              {showOldPassword ? (
                <EyeSlashIcon className='h-4 w-4 text-gray-400' />
              ) : (
                <EyeIcon className='h-4 w-4 text-gray-400' />
              )}
            </button>
          }
          isRequired
        />
        <Input
          label='New Password'
          placeholder='Enter new password'
          type={showNewPassword ? 'text' : 'password'}
          value={passwordData.password}
          onValueChange={value => onPasswordDataChange({ ...passwordData, password: value })}
          endContent={
            <button
              className='focus:outline-none'
              type='button'
              onClick={() => onShowNewPasswordChange(!showNewPassword)}
            >
              {showNewPassword ? (
                <EyeSlashIcon className='h-4 w-4 text-gray-400' />
              ) : (
                <EyeIcon className='h-4 w-4 text-gray-400' />
              )}
            </button>
          }
          isRequired
        />
        <Input
          label='Confirm New Password'
          placeholder='Confirm new password'
          type={showConfirmPassword ? 'text' : 'password'}
          value={passwordData.confirmPassword}
          onValueChange={value => onPasswordDataChange({ ...passwordData, confirmPassword: value })}
          endContent={
            <button
              className='focus:outline-none'
              type='button'
              onClick={() => onShowConfirmPasswordChange(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className='h-4 w-4 text-gray-400' />
              ) : (
                <EyeIcon className='h-4 w-4 text-gray-400' />
              )}
            </button>
          }
          isRequired
        />
      </div>
    </ModernModal>
  );
};
